import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    ENABLE_FULL_RESPONSIVE,
    IS_BLOCKED,
    IS_CHOSEN_PUBLIC_FILE,
    IS_FROZEN,
    IS_PUBLIC_FOLDER,
    PUBLIC_NOT_AVAILABLE,
    PUBLIC_NOT_EXISTS,
} from 'reactApp/appHelpers/configHelpers';
import { BREAKPOINT_MD } from 'reactApp/constants/breakpoints';
import { getAllDocumentsCurrentType } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentStorage, isSubscriptionPage } from 'reactApp/modules/router/router.selectors';
import { getSearchEmptyFilters } from 'reactApp/modules/search/search.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EHeaderColor, HeaderWrapper } from 'reactApp/ui/Header/HeaderWrapper';
import { LeftSide } from 'reactApp/ui/Header/LeftSide';
import { Logo } from 'reactApp/ui/Header/Logo/Logo';
import { RightSide } from 'reactApp/ui/Header/RightSide';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ViewerShareButton } from 'reactApp/ui/ViewerShareButton/ViewerShareButton';
import { useWindowWidth } from 'reactApp/ui/VirtualList/VirtualList.hooks';

import styles from './Header.css';

export const Header = memo(() => {
    const storage = useSelector(getCurrentStorage);
    const { isHome, isSearch, isAttaches, isGallery, isTrashBin, isAllDocuments, isPdfEdit, isIntegration } = getStorage(storage);
    const hideRightSide = !useSelector(getAllDocumentsCurrentType) && isAllDocuments;
    const windowWidth = useWindowWidth();
    const isRepsonsiveEnable = ENABLE_FULL_RESPONSIVE && windowWidth <= BREAKPOINT_MD;

    const emptyFilters = useSelector(getSearchEmptyFilters);
    const queryParams = useSelector(SettingsSelectors.getQueryParams);
    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    const hideHeader = isPhone && (isSubscriptionPage() || isHome || IS_PUBLIC_FOLDER || isAttaches || isGallery || isTrashBin || isSearch);

    const searchOnlyExpanded = isSearch || (isHome && emptyFilters) || (isAttaches && Boolean(queryParams?.query));
    const [isSearchExpanded, setSearchExpanded] = useState<boolean>(searchOnlyExpanded);

    const toggleSearch = useCallback((toggleState: boolean) => {
        setSearchExpanded(toggleState);
    }, []);

    useEffect(() => {
        if (!searchOnlyExpanded) {
            setSearchExpanded(false);
        }
    }, [searchOnlyExpanded, storage]);

    const content = useMemo(() => {
        if (IS_FROZEN || IS_BLOCKED || PUBLIC_NOT_EXISTS || PUBLIC_NOT_AVAILABLE || isIntegration) {
            return <Logo />;
        }

        if (isPhone && IS_CHOSEN_PUBLIC_FILE) {
            return (
                <>
                    <Logo />
                    <ViewerShareButton basic placement={tooltipPlacements.BOTTOM_RIGHT} />
                </>
            );
        }

        return (
            <>
                <LeftSide
                    storage={storage}
                    toggleSearch={toggleSearch}
                    isSearchExpanded={isSearchExpanded}
                    isResponsiveEnable={isRepsonsiveEnable}
                />
                {!hideRightSide && (
                    <RightSide
                        storage={storage}
                        toggleSearch={toggleSearch}
                        isSearchExpanded={isSearchExpanded}
                        searchOnlyExpanded={searchOnlyExpanded}
                        isResponsiveEnable={isRepsonsiveEnable}
                    />
                )}
            </>
        );
    }, [isPhone, isSearchExpanded, searchOnlyExpanded, storage, toggleSearch, isRepsonsiveEnable, hideRightSide]);

    if (hideHeader) {
        return null;
    }

    // на /integration должен быть мобильный вид хедера
    return (
        <HeaderWrapper
            className={classNames({ [styles.pdf]: isPdfEdit })}
            color={isPhone ? EHeaderColor.BLUE : EHeaderColor.WHITE}
            isPhone={isPhone || isIntegration}
        >
            {content}
        </HeaderWrapper>
    );
});

Header.displayName = 'Header';
